import { Injectable } from "@angular/core";
import * as Three from "three";
import { Vector3 } from "three";
import * as TWEEN from "@tweenjs/tween.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { Store } from "@ngrx/store";
import {
  selectCameraTarget,
  selectCameraZoomTarget,
} from "src/app/state/camera/camera.selector";
import { worldActions } from "src/app/state/world/world.actions";
import {settings} from "../../../settings/settings";

let renderLoopCallback: (timeDelta: number) => void;


@Injectable({
  providedIn: "root",
})
export class ThreeService {
  debug = false;
  canvasWidth = 0;
  canvasHeight = 0;

  private __scene: Three.Scene;
  private __renderer: Three.WebGLRenderer;
  public __camera: Three.PerspectiveCamera;
  canvas: HTMLCanvasElement;
  fieldOfView = 1;
  nearClippingPlane = 1;
  farClippingPlane = 55000;
  frameCount = 0;
  targetFrameRate = settings.framerate;
  lastTime = new Date().getMilliseconds();

  callbacks: (typeof renderLoopCallback)[] = [];

  cameraTarget$ = this.store.select(selectCameraTarget);
  zoomTarget$ = this.store.select(selectCameraZoomTarget);

  constructor(private store: Store) {
    this.cameraTarget$.subscribe((target) => {
      if (!this.__camera) return;
      this.__camera.position.x = target.x;
      this.__camera.position.y = target.y;
    });
    this.zoomTarget$.subscribe((absoluteZoom) => {
      if (!this.__camera) return;
      this.__camera.position.z = absoluteZoom;
    });
  }

  get camera() {
    return this.__camera;
  }

  get scene() {
    return this.__scene;
  }

  resize() {
    // if (this.debug) {
    //   this.canvasWidth = this.canvas.width;
    //   this.canvasHeight = this.canvas.height;
    // }
    // if (!this.debug) {
    // this.canvasWidth = window.innerWidth;
    // this.canvasHeight = window.innerHeight;
    // }

    if (!this.canvas) return

    this.canvasWidth = this.canvas.offsetWidth;
    this.canvasHeight = this.canvas.offsetHeight;
    this.__camera.aspect = this.canvasWidth / this.canvasHeight;
    // const target = new Three.Vector3(0, 0, 0);
    // const distance = this.__camera.position.distanceTo(target);
    // const fov = (this.__camera.fov * Math.PI) / 180;
    // const viewportHeight = 2 * Math.tan(fov / 2) * distance;
    // const viewportWidth =
    //   viewportHeight * (this.canvasWidth / this.canvasHeight);
    this.__camera.clearViewOffset()
    this.__camera.updateProjectionMatrix();
    this.__camera.focus = 100
    this.__renderer.setSize(this.canvasWidth, this.canvasHeight);
  }

  resetCamera(camera) {
    camera.position.y = 0;
    camera.position.x = 0;
    camera.position.z = 200;
    camera.lookAt(new Vector3(0, 0, 0));
  }

  addToScene(object: Three.Object3D) {
    this.__scene.add(object);
  }

  removeFromScene(object: Three.Object3D) {
    this.__scene.remove(object);
  }

  calculateMovementOnX(inputVal: number) {
    // Calculate the half-width of the frustum at distance Z
    const halfWidthFrustum =
      this.__camera.position.z * Math.tan(this.fieldOfView / 2);
    // Full width of the frustum at distance Z
    const fullWidthFrustum = 2 * halfWidthFrustum;
    // Movement on the X tape is proportional to the A_position
    const movementOnX = (inputVal / fullWidthFrustum) * fullWidthFrustum;
    return movementOnX;
  }

  pixelToObjectDistance(dist: Three.Vector2, objectZ: number) {
    const fovRad = Three.MathUtils.degToRad(this.camera.fov);

    const depth = this.camera.position.z - objectZ
    // World height at depth D
    const H = 2 * depth * Math.tan(fovRad / 2);
    const W = H * this.camera.aspect;

    const perPixelX = W / this.canvasWidth;
    const perPixelY = H / this.canvasHeight;

    const result = Math.sqrt((dist.x * perPixelX) ** 2 + (dist.y * perPixelY) ** 2);
    const newVec = dist.clone()
    newVec.setLength(result)
    return newVec
  }


  pixelToWorldDistance(dist: Three.Vector2) {
    const fovRad = Three.MathUtils.degToRad(this.camera.fov);

    const depth = this.camera.position.z
    // World height at depth D
    const H = 2 * depth * Math.tan(fovRad / 2);
    const W = H * this.camera.aspect;

    const perPixelX = W / this.canvasWidth;
    const perPixelY = H / this.canvasHeight;

    const result = Math.sqrt((dist.x * perPixelX) ** 2 + (dist.y * perPixelY) ** 2);
    const newVec = dist.clone()
    newVec.setLength(result)
    return newVec
  }

  createScene(
    canvas: HTMLCanvasElement,
    window,
    backgroundColor?: number,
    debug?: boolean,
  ) {
    this.canvas = canvas;
    this.debug = debug;
    this.__scene = new Three.Scene();
    this.__scene.background = new Three.Color(backgroundColor ?? 0x999900ff);
    let aspectRatio = this.canvasWidth / this.canvasHeight;
    this.__camera = new Three.PerspectiveCamera(
      this.fieldOfView,
      aspectRatio,
      this.nearClippingPlane,
      this.farClippingPlane,
    );
    window.addEventListener("resize", this.resize.bind(this));
    this.resetCamera(this.__camera);
    this.store.dispatch(worldActions.threeRendererReady({ viewId: "main" }));
  }

  onRender(func: typeof renderLoopCallback) {
    this.callbacks.push(func);
  }

  async startRenderLoop() {
    this.__renderer = new Three.WebGLRenderer({ canvas: this.canvas });
    this.__renderer.shadowMap.enabled = true;
    this.__renderer.shadowMap.type = Three.PCFShadowMap;
    this.__renderer.setPixelRatio(devicePixelRatio);
    this.__renderer.outputColorSpace = Three.LinearSRGBColorSpace;
    this.resize();
    await this.setupLight();
    await this.__render();
    // const controls = new OrbitControls( this.camera, this.__renderer.domElement );
  }

  // light: Three.DirectionalLight
  light: Three.SpotLight;
  shadowPlane: Three.Mesh;
  lhelper: any;

  async setupLight() {
    // this.light = new Three.DirectionalLight( 0xffffff, 10 );
    this.light = new Three.SpotLight(0xffeedd, 110);
    this.light.position.set(0, 0, 10); //default; light shining from top
    this.light.angle = 0.005;
    this.light.penumbra = 1;
    this.light.decay = 0.5;
    // this.light.castShadow = false; // default false
    // this.light.shadow.mapSize.width = 1000; // default
    // this.light.shadow.mapSize.height = 1000; // default
    // this.light.shadow.camera.near = 1;
    // this.light.shadow.camera.far = 40000;
    // this.light.shadow.camera.fov = 0.1;
    this.light.lookAt(new Vector3(0, 0, 0));

    this.light.position.set(0, 0, 100);
    this.scene.add(this.light);

    const hemi = new Three.HemisphereLight(0xddeeff, 0x181820, 5);
    this.scene.add(hemi);

    const planeGeometry = new Three.PlaneGeometry(200, 200, 100, 100);
    const planeMaterial = new Three.MeshStandardMaterial({ color: 0x0e0e0e });
    this.shadowPlane = new Three.Mesh(planeGeometry, planeMaterial);
    this.shadowPlane.position.z = -0.01;
    this.shadowPlane.receiveShadow = true;
    // this.scene.add( this.shadowPlane );
  }

  async __render() {
    const myFunc = async (timestamp) => {
      requestAnimationFrame(myFunc);
      TWEEN.update(timestamp);
      const camPos = this.__camera.position.clone();
      this.light.position.set(camPos.x, camPos.y, camPos.z); //default; light shining from top
      this.light.target.position.set(camPos.x, camPos.y, 0);
      this.light.updateMatrix();
      this.light.target.updateMatrixWorld();
      // this.lhelper.update()
      const singleFrameTime = 1 / this.targetFrameRate;
      const timeDelta = Math.min(
        (timestamp - this.lastTime) / singleFrameTime,
        3,
      );
      this.callbacks.forEach(async (func) => {
        await func(timeDelta);
      });
      this.__renderer.render(this.__scene, this.__camera);
      this.lastTime = timestamp;
    };
    requestAnimationFrame(myFunc);
  }
}
